import React, { ComponentProps, Fragment, useMemo } from "react";
import styled from "styled-components";
import {
  Flex,
  Text,
  Box,
  Button,
  Tooltip,
  ItemType,
  useWindowSize,
  useBoolean,
} from "@thenounproject/lingo-core";

import useInspectableActions from "@features/context-menus/hooks/useInspectableActions";
import InspectorMultiAssetDownload from "./use/InspectorMultiAssetDownload";
import ActionsMenu, { useActionMenuTrigger } from "@features/context-menus/ActionMenu";

import { Inspectable, isAssetInspectable } from "@constants/Inspector";
import { useViewModeContext } from "@contexts/ViewModeProvider";

const DownloadAssetWrapper = styled(Box).attrs({ ml: "l" })``;
const TitleText = styled(Text).attrs({
  font: "ui.regularBold",
})`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ControlsContainer = styled(Flex).attrs({ alignItems: "center" })`
  flex-shrink: 0;
`;

const ActionButton = styled(Button).attrs({
  size: "small",
  mr: "s",
  themeOverrides: {
    primaryColor: "grayLightest",
    primaryColorTint: "black",
    primaryColorDark: "grayLight",
  },
})``;

export type Props = {
  inspectables: Inspectable[];
  canEdit: boolean;
};

const InspectorSimpleMultiple: React.FC<Props> = ({ inspectables, canEdit }) => {
  const { belowInspectorThreshold } = useWindowSize();
  const { setInspectorMode } = useViewModeContext();
  const [isActionMenuOpen, openActionMenu, closeActionMenu] = useBoolean(false);

  const hasFiles = inspectables.some(i => i?.asset?.permalink);

  const { deleteAssets, deleteItems, trashItems, addToKit } = useInspectableActions(inspectables);
  const hasAssets = useMemo(
    () => inspectables.some(i => !i.item || i.item?.type === ItemType.asset),
    [inspectables]
  );
  const hasDownloadableAssets = useMemo(
    () =>
      inspectables.some(
        i => isAssetInspectable(i) && i.item?.displayProperties?.allowDownload !== false
      ),
    [inspectables]
  );

  useActionMenuTrigger(() => {
    openActionMenu();
    return true;
  });

  function renderTitleSection() {
    return <TitleText>{`${inspectables.length} items selected`}</TitleText>;
  }

  function renderControlsSection() {
    function renderActionButton() {
      return (
        <>
          <ActionsMenu
            open={isActionMenuOpen}
            close={closeActionMenu}
            source="action-menu-button-multiple"
            inspectables={inspectables}
            vPos="alignBottom"
            hPos="alignLeft"
          />
          <Tooltip source="action-menu-button-multiple" direction={Tooltip.Direction.Top}>
            Actions (⌘K)
          </Tooltip>
          <ActionButton
            data-tooltip-source="action-menu-button-multiple"
            data-popup-source="action-menu-button-multiple"
            aria-label="Actions"
            icon="action.quick-action"
            onClick={openActionMenu}
          />
        </>
      );
    }

    function viewDetailsButton() {
      if (belowInspectorThreshold) return null;
      return (
        <>
          <Tooltip source="view-details-button" direction={Tooltip.Direction.Top}>
            View details
          </Tooltip>
          <ActionButton
            data-tooltip-source="view-details-button"
            aria-label="View details"
            icon="info.info"
            onClick={() => setInspectorMode("detailed")}
          />
        </>
      );
    }

    function addToKitButton() {
      if (!canEdit) return null;
      if (!addToKit) return null;
      return (
        <>
          <Tooltip source="add-to-button" direction={Tooltip.Direction.Top}>
            Add to
          </Tooltip>
          <ActionButton
            data-tooltip-source="add-to-button"
            icon="action.add-to"
            aria-label="Add to kit"
            onClick={addToKit}
          />
        </>
      );
    }

    function deleteButton() {
      if (!canEdit) return null;
      const buttonProps: Partial<ComponentProps<typeof ActionButton>> = {};
      if (deleteAssets) {
        buttonProps.onClick = deleteAssets;
        buttonProps.title = "Delete";
      } else if (trashItems) {
        // Don't show remove from kit button for assets
        return null;
      } else if (deleteItems) {
        buttonProps.onClick = deleteItems;
        buttonProps.title = "Delete";
      } else {
        return null;
      }
      return (
        <>
          <Tooltip source="delete-assets-button" direction={Tooltip.Direction.Top}>
            {buttonProps.title}
          </Tooltip>
          <ActionButton
            data-tooltip-source="delete-assets-button"
            aria-label="Delete"
            icon="action.trash"
            {...buttonProps}
          />
        </>
      );
    }

    function downloadButton() {
      if (!hasAssets) return null;
      let el = null;
      const downloadableInspectables = hasDownloadableAssets
        ? inspectables.filter(
            i => isAssetInspectable(i) && i?.item?.displayProperties?.allowDownload !== false
          )
        : [];
      if (hasFiles) {
        const assetDownloadProps = {
          inspectables: downloadableInspectables,
        };
        el = <InspectorMultiAssetDownload {...assetDownloadProps} />;
      } else {
        el = (
          // We need this wrapper because disabled elements don't fire the events
          // that the tooltip relies on
          <Box data-tooltip-source="no-downloads">
            <Button text="Download" disabled icon="download" size="small" />
            <Tooltip source="no-downloads" direction={Tooltip.Direction.Top}>
              No files to download
            </Tooltip>
          </Box>
        );
      }

      return el && <DownloadAssetWrapper>{el}</DownloadAssetWrapper>;
    }

    return (
      <ControlsContainer>
        <Fragment>
          {renderActionButton()}
          {addToKitButton()}
          {deleteButton()}
          {viewDetailsButton()}
          {downloadButton()}
        </Fragment>
      </ControlsContainer>
    );
  }

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      data-testid="inspector-simple-multiple">
      {renderTitleSection()}
      {renderControlsSection()}
    </Flex>
  );
};

export default InspectorSimpleMultiple;
